import $ from 'jquery';
import { adminsAjaxSelect2 } from 'lib/select2';

// 管理画面の「大学・高校などを大量の候補から複数選ぶ」フォーム部品。
//
// 旧 UI は候補を全件チェックボックスとして描画していたため（大学 5,000 件超、イベント 28,000 件超）、
// HTML が数十 MB になりフィルタ入力のたびにレイアウト再計算が走っていた。
// 新 UI は選択済みの候補だけを <option> として持ち、候補は /api/v3/admins/options/* から都度取得する。
//
// マークアップはビュー側（app/views/admins/shared/_options_select_v2.html.haml）を参照。
//   .js-admins-options-select[data-url][data-collection-key][data-label-key][data-value-key][data-hidden-field][data-tags][data-extra-params]
//     input.js-admins-options-select-bulk[data-param][data-value]  学群などの一括追加チェックボックス
//     select.js-admins-options-select-input[multiple]            選択済み候補。name があればそのまま配列で送信される
//     .js-admins-options-select-summary                          選択数が多いときの折りたたみ表示
//   data-hidden-field があるときは選択値をカンマ区切りでその hidden input にも書く（ransack / *_ids_string 互換）

type Candidate = Record<string, unknown>;

interface Config {
  url: string;
  collectionKey: string;
  labelKey: string;
  valueKey: string;
  hiddenFieldId: string | null;
  tags: boolean;
  extraParams: Record<string, string>;
}

// この件数を超えたらタグ表示を畳んで件数だけ出す（送信値は select / hidden に残る）
const COLLAPSE_THRESHOLD = 200;

type Select2Event = { params?: { data?: { id: string } } };

const readConfig = (root: HTMLElement): Config => {
  const data = root.dataset;
  return {
    url: data.url as string,
    collectionKey: data.collectionKey as string,
    labelKey: data.labelKey as string,
    valueKey: data.valueKey as string,
    hiddenFieldId: data.hiddenField || null,
    tags: data.tags === 'true',
    extraParams: data.extraParams ? (JSON.parse(data.extraParams) as Record<string, string>) : {},
  };
};

const fetchCandidates = async (config: Config, params: Record<string, string>): Promise<Candidate[]> => {
  const query = new URLSearchParams({ ...config.extraParams, ...params });
  const response = await fetch(`${config.url}?${query.toString()}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`候補の取得に失敗しました (${response.status})`);
  const json = (await response.json()) as Record<string, unknown>;
  const list = json[config.collectionKey];
  return Array.isArray(list) ? (list as Candidate[]) : [];
};

const initOptionsSelect = (root: HTMLElement) => {
  const config = readConfig(root);
  const $select = $(root).find('select.js-admins-options-select-input');
  const select = $select.get(0) as HTMLSelectElement | undefined;
  if (!select) return;
  const $hidden = config.hiddenFieldId ? $(`#${config.hiddenFieldId}`) : $();
  const $summary = $(root).find('.js-admins-options-select-summary');
  const $count = $summary.find('.js-admins-options-select-count');
  let expanded = false;
  // 一括チェックの応答をキャッシュして、解除時に「他の学群に含まれる候補は残す」判定にも使う
  const bulkCache: Record<string, Candidate[]> = {};
  // 名前検索で手で選んだ候補と、サーバーが描画した選択済み候補。学群の解除では消さない
  const pinnedValues = new Set<string>();
  Array.from(select.selectedOptions).forEach((option) => pinnedValues.add(option.value));

  adminsAjaxSelect2($select, {
    url: config.url,
    collectionKey: config.collectionKey,
    toOption: (item) => ({ id: String(item[config.valueKey]), text: String(item[config.labelKey]) }),
    inputTooShort: '1文字以上入力してください',
    extraParams: config.extraParams,
    // 選んだあとも候補を閉じない。大学とその大学院のように、同じ検索語で複数の候補を続けて選ぶ操作が多く、
    // 選ぶたびに閉じて検索語が消えると入力をやり直すことになるため（開いたままの候補は再クリックで外せる）
    select2Options: { tags: config.tags, closeOnSelect: false },
  });

  const selectedValues = (): string[] => Array.from(select.selectedOptions, (option) => option.value);

  $select.on('select2:select', (e: Select2Event) => {
    const data = e.params?.data;
    if (data && data.id !== '') pinnedValues.add(String(data.id));
  });
  $select.on('select2:unselect', (e: Select2Event) => {
    const data = e.params?.data;
    if (data) pinnedValues.delete(String(data.id));
  });

  const $toggle = $summary.find('.js-admins-options-select-expand');
  // 状態は has-selection（選択あり）/ has-many（閾値超、畳む・表示の切り替え可）/ is-collapsed（畳んでいる）の 3 クラスで CSS に伝える
  const sync = () => {
    const values = selectedValues();
    if ($hidden.length > 0) $hidden.val(values.join(','));
    $count.text(values.length.toLocaleString());
    const manyValues = values.length > COLLAPSE_THRESHOLD;
    if (!manyValues && expanded) {
      expanded = false;
      $toggle.text('タグを表示');
    }
    root.classList.toggle('has-selection', values.length > 0);
    root.classList.toggle('has-many', manyValues);
    root.classList.toggle('is-collapsed', manyValues && !expanded);
  };

  // 一括追加。既存 option は Map で引き、新規 option は fragment でまとめて追加する（候補 × option の総当たりを避ける）
  const addCandidates = (candidates: Candidate[]) => {
    const existing = new Map(Array.from(select.options, (option) => [option.value, option]));
    const fragment = document.createDocumentFragment();
    candidates.forEach((candidate) => {
      const value = String(candidate[config.valueKey]);
      const option = existing.get(value);
      if (option) {
        option.selected = true;
      } else {
        const created = new Option(String(candidate[config.labelKey]), value, true, true);
        existing.set(value, created);
        fragment.appendChild(created);
      }
    });
    select.appendChild(fragment);
    $select.trigger('change');
  };

  // 一括解除。候補として表示されなくなった option は DOM からも外す（select2 が change のたびに走査する option を減らす）
  const removeValues = (values: string[]) => {
    const removing = new Set(values);
    Array.from(select.options).forEach((option) => {
      if (removing.has(option.value)) option.remove();
    });
    $select.trigger('change');
  };

  // 学群などの一括チェック。チェック時は API で取得して追加、
  // 解除時は「他にチェック中の一括条件に含まれる候補」と「手で選んだ候補」を残してそれ以外を除く
  $(root).on('change', '.js-admins-options-select-bulk', (e: Event) => {
    const checkbox = e.currentTarget as HTMLInputElement;
    const key = `${checkbox.dataset.param}=${checkbox.dataset.value}`;
    const load = bulkCache[key]
      ? Promise.resolve(bulkCache[key])
      : fetchCandidates(config, { [checkbox.dataset.param || '']: checkbox.dataset.value || '' }).then((list) => {
          bulkCache[key] = list;
          return list;
        });
    load
      .then((list) => {
        if (checkbox.checked) {
          addCandidates(list);
          return;
        }
        const keep = new Set<string>(pinnedValues);
        $(root)
          .find('.js-admins-options-select-bulk:checked')
          .each((_: number, other: HTMLElement) => {
            const otherKey = `${(other as HTMLInputElement).dataset.param}=${(other as HTMLInputElement).dataset.value}`;
            (bulkCache[otherKey] || []).forEach((candidate) => keep.add(String(candidate[config.valueKey])));
          });
        removeValues(list.map((candidate) => String(candidate[config.valueKey])).filter((value) => !keep.has(value)));
      })
      .catch(() => {
        checkbox.checked = !checkbox.checked;
        window.alert('候補の取得に失敗しました。時間をおいて再度お試しください。');
      });
  });

  $toggle.on('click', (e: Event) => {
    e.preventDefault();
    expanded = !expanded;
    $toggle.text(expanded ? 'タグを畳む' : 'タグを表示');
    sync();
  });
  $summary.on('click', '.js-admins-options-select-clear', (e: Event) => {
    e.preventDefault();
    pinnedValues.clear();
    $select.val(null).trigger('change');
    $(root).find('.js-admins-options-select-bulk').prop('checked', false);
  });

  $select.on('change', sync);
  sync();
};

window.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll<HTMLElement>('.js-admins-options-select').forEach((root) => initOptionsSelect(root));
});
